import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { SubmitButton } from "components/common/buttons/SubmitButton";
import { Select } from "components/common/Select";
import { fetchLanguages } from "slices/languageSlice";
import { languageSelector } from "slices/languageSlice";
import { setError } from "slices/dashboardSlice";
import { AsyncPaginate } from 'react-select-async-paginate';
import { selectCustomStyles } from 'components/common/Select';
import FullPageLoader from "components/common/FullPageLoader";
import ListTable from "components/vendorAccess/dashboard/ListTable";
import { VendorDashboardSelector } from "slices/vendor/dashboardSlice";
import { fetchVendorDashboardAnalytics } from "slices/vendor/dashboardSlice";
import { fetchVendorDashboardAppointments, downloadVendorDashboardReport } from "slices/vendor/dashboardSlice";
import { LegendButton } from "components/common/TableColor";
import { vendorLegends } from "components/common/TableColor";
import Swal from "sweetalert2";
import DateRangeFilter from "components/common/DateRangeFilter";
import { fetchVendorInterpreterOptions } from "slices/interpreterSlice";
import { fetchSchools } from "slices/schoolSlice";
import { schoolSelector } from "slices/schoolSlice";
import ErrorMessage from "components/common/ErrorMessage";


function Dashboard() {
  const dispatch = useDispatch()
  const { vendorDashboard: dashboard, last_page: totalPages, loader, total } = useSelector(VendorDashboardSelector);
  const { language: languages } = useSelector(languageSelector);
  const { school: schools } = useSelector(schoolSelector);
  const { clearErrors, handleSubmit, watch, formState: { errors }, control, setValue, setError: setErrors, reset, getValues } = useForm({
    defaultValues: {
      start_date: moment().format('YYYY-MM-DD'),
      end_date: moment().format('YYYY-MM-DD'),
      interpreter: null,
    }
  });
  const [filters, setFilters] = useState({
    start_date: moment().format('YYYY-MM-DD'),
    end_date: moment().format('YYYY-MM-DD'),
  });
  const [activeLegends, setActiveLegends] = useState([0]);
  const [statusValues, setStatusValues] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const interpreters = useSelector((state) => state.interpreter.interpreter);

  useEffect(() => {
    dispatch(fetchVendorDashboardAnalytics())
    dispatch(fetchVendorDashboardAppointments(1, { pageLength: 100, start_date: moment().format('YYYY-MM-DD'), end_date: moment().format('YYYY-MM-DD'), sortBy: "date", sortDirection: "asc" }))
  }, [])


  // #region Language dropdown 
  // const filteredLanguages = React.useMemo(() =>
  //   languages?.map(language => {
  //     const object = {
  //       value: language?.id,
  //       label: language?.name
  //     }
  //     return object
  //   }), [languages]);

  // const filteredLanguagesLoadOptions = async (inputValue, loadedOptions, { page }) => {
  //   const result = await dispatch(fetchLanguages(page, { pageLength: 20, sortBy: "id", sortDirection: "asc", search: inputValue }, true, true))
  //   return {
  //     options: result.data.map((item) => ({
  //       value: item.id,
  //       label: item.name,
  //     })),
  //     hasMore: page !== result?.last_page ? true : false,
  //     additional: {
  //       page: page + 1,
  //     },
  //   };
  // };

  const [languageState, setLanguageState] = useState({
    label: "",
    value: ""
  })

  useEffect(() => {
    setLanguageState(
      languages?.map(val => ({
        label: val?.language?.name || "",
        value: val?.language?.id || ""
      }))
    );
  }, [languages]);


  const filteredLanguagesLoadOptions = async (inputValue, loadedOptions, { page }) => {
    const result = await dispatch(fetchLanguages(page, { pageLength: 20, sortBy: "id", sortDirection: "asc", search: inputValue }, true, true));
    return {
      options: result?.data?.map(language => ({
        value: language.id,
        label: language.name,
      })),
      hasMore: page !== result?.last_page,
      additional: {
        page: page + 1,
      },
    };
  };
  //#endregion




  // #region school dropdown 
  const filteredSchools = React.useMemo(() =>
    schools?.map(language => {
      const object = {
        value: language?.id,
        label: language?.name
      }
      return object
    }), [schools]);

  const filteredSchoolLoadOptions = async (inputValue, loadedOptions, { page }) => {
    const result = await dispatch(fetchSchools(page, { pageLength: 20, sortBy: "id", sortDirection: "asc", search: inputValue }, true, true))
    return {
      options: result.data.map((item) => ({
        value: item.id,
        label: item.name,
      })),
      hasMore: page !== result?.last_page ? true : false,
      additional: {
        page: page + 1,
      },
    };
  };
  //#endregion



  // #region Legends 
  const filteredStatuses = React.useMemo(() => {
    const temp_array = [...vendorLegends];
    temp_array.unshift({
      value: "all",
      name: "All",
      id: 0,
      color: "#3176A0"
    })
    return temp_array;
  }, [vendorLegends]);

  const hanldeActiveLegend = (id, value) => {
  if (value === "all") {
    setActiveLegends([id]);
    setStatusValues([]);

    const allFilters = {
      ...getValues(),
      pageLength: 100,
    };

    dispatch(fetchVendorDashboardAppointments(1, allFilters));
  } else {
    setActiveLegends((prev) => {
      const withoutAll = prev.filter((pid) => pid !== 0);

      let newActive, newStatuses;
      if (withoutAll.includes(id)) {
        newActive = withoutAll.filter((item) => item !== id);
        newStatuses = statusValues.filter((s) => s !== value);
      } else {
        newActive = [...withoutAll, id];
        newStatuses = [...statusValues, value];
      }

      setStatusValues(newStatuses);

      // ✅ include other filters like school_id, interpreter, type, etc.
      const currentFilters = {
        ...getValues(),
        pageLength: 100,
        status: newStatuses.length ? newStatuses : undefined,
      };
      setFilters(currentFilters);

      dispatch(fetchVendorDashboardAppointments(1, currentFilters));

      return newActive;
    });
  }
};


  //#endregion


  const onSubmit = (data) => {
    dispatch(setError({}))
    const params = data.appointment_id
      ? { pageLength: 100, sortBy: 'date', sortDirection: 'desc', appointment_id: data.appointment_id }
      : { ...data, pageLength: 100, status: statusValues };
    setFilters(params);
    dispatch(fetchVendorDashboardAppointments(1, params))
    clearErrors();
  };


  const handleResetFields = () => {
    const today = moment().format("YYYY-MM-DD");
    setLanguageState([]);
    setValue("start_date", today);
    setValue("end_date", today);
    setValue("language_id", "");
    setValue("account", "");
    setValue("department", "");
    setValue("facility", "");
    setValue("service", "");
    setValue("language_ids", []);
    setActiveLegends([0]);
    setStatusValues([]);

    reset({
      interpreter: null,
      language_ids: [],
      start_date: today,
      end_date: today,
      appointment_id: '',
    });

    const resetParams = {
      pageLength: 100,
      sortBy: "date",
      sortDirection: "asc",
      start_date: today,
      end_date: today,
    };
    setFilters(resetParams);
    dispatch(fetchVendorDashboardAppointments(1, resetParams));

    clearErrors();
  };

  const downloadBlobFile = (blob, filename = "appointments_report.xlsx") => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadReport = () => {
    setDownloading(true);


    const filters = {
      ...getValues(),
      status: statusValues
    };

    dispatch(
      downloadVendorDashboardReport(filters, (success, res) => {
        setDownloading(false);

        if (!success) {
          Swal.fire({
            title: "Error!",
            text: (res && res.message) || "Failed to download report",
            icon: "error",
            confirmButtonColor: "#d33",
          });
          return;
        }

        try {
          if (res instanceof Blob) {
            downloadBlobFile(res);
          } else if (res && res.blob) {
            downloadBlobFile(res.blob, res.filename || "dashboard_report.xlsx");
          } else if (typeof res === "string" && res.startsWith("data:")) {
            const arr = res.split(",");
            const mimeMatch = arr[0].match(/:(.*?);/);
            const mime = mimeMatch ? mimeMatch[1] : "application/octet-stream";
            const bstr = atob(arr[1]);
            let n = bstr.length;
            const u8 = new Uint8Array(n);
            while (n--) u8[n] = bstr.charCodeAt(n);
            const blob = new Blob([u8], { type: mime });
            downloadBlobFile(blob);
          } else if (typeof res === "string") {
            const a = document.createElement("a");
            a.href = res;
            a.target = "_blank";
            a.download = "";
            document.body.appendChild(a);
            a.click();
            a.remove();
          }

          Swal.fire({
            title: "Success!",
            text: "Report downloaded successfully.",
            icon: "success",
            confirmButtonColor: "#3085d6",
          });
        } catch (err) {
          console.error("Download error:", err);
          Swal.fire({
            title: "Error!",
            text: "Could not process downloaded file",
            icon: "error",
            confirmButtonColor: "#d33",
          });
        }
      })
    );
  };
  const [isFourteenDays, setIsFourteenDays] = useState(false);
  const tableRef = useRef(null);

  const handleFilter = (item, isforteen) => {
    const today = moment().format('YYYY-MM-DD');
    const tomorrow = moment().add(1, 'days').format('YYYY-MM-DD');
    const next_fourteen_days = moment().add(14, 'days').format('YYYY-MM-DD');

    if (isforteen) {
      setValue("start_date", tomorrow);
      setValue("end_date", next_fourteen_days);
    } else {
      setValue("start_date", today);
      setValue("end_date", today);
    }

    if (item?.value) {
      setValue("service", item?.value);
    } else {
      setValue("service", 0);
    }

    const data = {
      pageLength: 100,
      sortBy: "date",
      sortDirection: "asc",
      start_date: isforteen ? tomorrow : today,
      end_date: isforteen ? next_fourteen_days : today,
      service: item?.value || 0,
      interpreter: item.interpreter ?? null,
    };

    setFilters(data);
    dispatch(fetchVendorDashboardAppointments(1, data));
    tableRef.current.scrollIntoView({ behavior: "smooth" });
    dispatch(setError({}));
    clearErrors();
  };

  useEffect(() => {
    (async () => {
      const result = await dispatch(fetchVendorInterpreterOptions());
      console.log("Prefetched interpreters:", result);
    })();
  }, [dispatch]);


  const interpreterOptions = React.useMemo(
    () =>
      interpreters?.map((i) => ({
        value: i.id,
        label:
          i.user?.name ||
          `${i.first_name ?? ""} ${i.last_name ?? ""}`.trim() ||
          "Unknown",
      })) || [],
    [interpreters]
  );

  const interpreterLoadOptions = async (inputValue, loadedOptions, { page }) => {
    return await dispatch(
      fetchVendorInterpreterOptions(page, {
        pageLength: 100,
        sortBy: "id",
        sortDirection: "asc",
        search: inputValue,
      })
    );
  };

  console.log(total, 'totalllllllll')

  return (
    <>
      <div className="content">
        <div className="row_main_head">
          <h2 className="main_heading">Dashboard</h2>
        </div>

        <div className="row_dash_1">
          <div className="card_table">
            <div className="interpreter p-4">
              <h2>Today</h2>
            </div>

            <div className="Table_data_show pt-0">
              <table>
                <thead>
                  <tr>
                    <th className="col-6">Today</th>
                    <th className="col-3 text-center">Registered</th>
                    <th className="col-3 text-center">To Cover</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard?.analytics?.todays_data?.map((service, index) => (
                    <tr
                      key={index}
                      onClick={() => {
                        setIsFourteenDays(false);
                        handleFilter(service, false);
                      }}
                    >
                      <td>{service?.type}</td>
                      <td className="text-center">{service?.assigned}</td>
                      <td className="text-center">{service?.pending}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card_table">
            <div className="interpreter p-4">
              <h2>Next 14 Days</h2>
            </div>

            <div className="Table_data_show pt-0">
              <table>
                <thead>
                  <tr>
                    <th className="col-6">Next 14 Days</th>
                    <th className="col-3 text-center">Registered</th>
                    <th className="col-3 text-center">To Cover</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard?.analytics?.fourteen_days?.map((service, index) => (
                    <tr
                      key={index}
                      onClick={() => {
                        setIsFourteenDays(true);
                        handleFilter(service, true);
                      }}
                    >
                      <td>{service?.type}</td>
                      <td className="text-center">{service?.assigned}</td>
                      <td className="text-center">{service?.pending}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* <ScheduleModal /> */}
        <div className="card_table">
          <div className="button-sm forLegends p-3 gap-1">
            <LegendButton activeIds={activeLegends} options={filteredStatuses} handleClick={hanldeActiveLegend} />
          </div>
          <form className="search_form_ad" onSubmit={handleSubmit(onSubmit)}>
            <div className="row-primary dahboard_filter">
              <DateRangeFilter control={control} errors={errors} />
              <div className="row-primary">
                <h2>
                  Interpreter
                </h2>
                <div style={{ flex: 1 }}>
                  <Controller
                    name="interpreter"
                    control={control}
                    defaultValue={null}
                    render={({ field }) => (
                      <div className="basic_bases">
                        <AsyncPaginate
                          menuPlacement="auto"
                          styles={{ ...selectCustomStyles }}
                          value={
                            field.value
                              ? interpreterOptions.find((opt) => String(opt.value) === String(field.value))
                              : null
                          }
                          loadOptions={interpreterLoadOptions}
                          isClearable
                          escapeClearsValue
                          onChange={(obj) => field.onChange(obj?.value ?? null)}
                          additional={{ page: 1 }}
                        />
                      </div>
                    )}
                  />
                </div>
              </div>
              <div className="row-primary">
                <h2>Languages</h2>
                <Controller
                  name="language_ids"
                  control={control}
                  render={({ field }) =>
                    <div style={{ width: "100%" }}>
                      <div style={{ width: "100%" }} className="multiselect">
                        <AsyncPaginate
                          menuPlacement='auto'
                          isMulti
                          styles={{
                            ...selectCustomStyles,
                          }}
                          value={languageState}
                          loadOptions={filteredLanguagesLoadOptions}
                          isClearable
                          escapeClearsValue
                          onChange={(selected) => {
                            setLanguageState(selected)
                            field.onChange(selected.map((opt) => opt.value))
                          }}
                          additional={{
                            page: 1,
                          }}
                        />
                      </div>
                    </div>
                  }
                />
              </div>
              <div className="row-primary">
                <h2>School</h2>
                <Controller
                  name="school_id"
                  control={control}
                  render={({ field }) =>
                    <div style={{ width: "100%" }}>
                      <AsyncPaginate
                        menuPlacement='auto'
                        styles={{
                          ...selectCustomStyles,
                        }}
                        value={field.value ? filteredSchools?.find((opt) => field.value == opt.value) : ""}
                        loadOptions={filteredSchoolLoadOptions}
                        isClearable
                        escapeClearsValue
                        onChange={(obj) => {
                          field.onChange(obj?.value);
                        }}
                        additional={{
                          page: 1,
                        }}
                      />
                    </div>
                  }
                />
              </div>
              <div className="row-primary">
                <h2>Type</h2>
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <div style={{ width: "100%" }}>
                      <AsyncPaginate
                        {...field}
                        styles={selectCustomStyles}
                        options={[
                          { value: 0, label: "Interpreting Request" },
                          { value: 1, label: "After Hours Request" },
                        ]}
                        isClearable
                        placeholder="Select Type"
                        onChange={(option) => field.onChange(option ? option.value : null)}
                        value={
                          field.value !== null && field.value !== undefined
                            ? [
                              { value: 0, label: "Interpreting Request" },
                              { value: 1, label: "After Hours Request" },
                            ].find((opt) => opt.value === field.value)
                            : null
                        }
                      />
                    </div>
                  )}
                />
              </div>
            </div>

            <div className="row-primary dahboard_filter">
              <div className="row-primary">
                <h2>Appointment ID</h2>
                <Controller
                  name="appointment_id"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      className="input-in type_inp"
                      placeholder="e.g. A19956 or 19956"
                    />
                  )}
                />
              </div>
            </div>

            <div className="d-flex justify-content-between btns_area">
              <div className="row-primary row_10">
                <SubmitButton buttonType="submit" btnText="Search" className={'green'} />
                <SubmitButton buttonType="button" className={'lightBlue'} btnText="Reset" handleSubmit={handleResetFields} />
              </div>
              <button
                type="button"
                style={{ height: "fit-content", marginLeft: "10px" }}
                className="main_button"
                onClick={handleDownloadReport}
              >
                Export to Excel
              </button>
            </div>

            <div ref={tableRef}>
              <ListTable data={dashboard?.appointments} filtration={filters} total={total}/>
            </div>

          </form>
        </div>
      </div>

      {loader && <FullPageLoader />}
    </>
  );
}

export default Dashboard;